import React, { useContext, useRef } from 'react';
import Panel from "../structure/Panel";
import Button from "../button/Button";
import { FormikStepProps } from "../navigation/FormikStep";
import { EmployeePages } from "../enums/EmployeePages";
import LoadingSpinner from '../structure/LoadingSpinner';
import { summarize } from "../support/FormikSummarizer";
import NextSteps from "../support/NextSteps";
import { PdsButton, PdsGrid, PdsGridItem } from "@principal/design-system-react";
import { PdsIconEdit } from "@principal/design-system-icons-react";
import { FormikValues, useFormikContext } from "formik";
import Signature from "../input/Signature";
import { LifeBeneficiaryFormValues } from "../life/LifeBeneficiaryFormValues";
import { Party } from "../enums/Party";
import _ from "lodash";
import Agreements from "./Agreements";
import { Agreement } from "../enums/Agreement";
import { EmployeeFormValues } from "./EmployeeFormValues";
import { EmployerFormValues } from "./EmployerFormValues";
import { PhysicianFormValues } from "./PhysicianFormValues";
import { BeneficiaryFormValues } from "./BeneficiaryFormValues";
import { StepContext } from "../navigation/FormikStepper";
import { PfmlEmployeeFormValues } from "../pfml/PfmlEmployeeFormValues";
import { HospitalIndemnityEmployeeFormValues } from "../indemnity/HospitalIndemnityEmployeeFormValues";
import { DisabilityEmployeeFormValues } from "../disability/DisabilityEmployeeFormValues";
import { CriticalIllnessEmployeeFormValues } from "../illness/CriticalIllnessEmployeeFormValues";
import { AccidentEmployeeFormValues } from "../accident/AccidentEmployeeFormValues";

type SummaryProps = {
    nextSteps: string[],
    ownsClaim?: boolean,
    children?: React.ReactNode
}

const Summary = (props: SummaryProps) => {
    const {
        initialValues,
        values,
        isSubmitting
    } = useFormikContext<EmployeeFormValues | EmployerFormValues | PhysicianFormValues | BeneficiaryFormValues | AccidentEmployeeFormValues
        | DisabilityEmployeeFormValues
        | CriticalIllnessEmployeeFormValues
        | HospitalIndemnityEmployeeFormValues
        | PfmlEmployeeFormValues>()
    const { steps, stepChangeCallback, stepRefs } = useContext(StepContext)
    const summary = useRef(null);

    const party = values.party;
    const isUnitedStatesPerson = (values as LifeBeneficiaryFormValues)?.isUnitedStatesPerson === 'Yes';
    const agreements = Agreement.findAgreementsFor(initialValues, values);
    const needsSignature = isUnitedStatesPerson || agreements.some(agreement => Agreement.isSignatureRequired(agreement));
    const agreementsNotAgreedTo = agreements
        .filter(agreement => agreement !== Agreement.HIPAA_AUTHORIZATION)
        .some(agreement => agreementNotAgreedTo(values, agreement))
    const signatureId = `${Party.EMPLOYEE === party ? '' : `${party}-`}signature`;
    const signatureRequiredAndNotSigned = needsSignature && '' === _.get(values, _.camelCase(signatureId))
    const ownsClaim = props.ownsClaim;
    const signaturePrefix = ownsClaim ? 'Your signature' : `Signature of ${party}`
    const signatureLabel = `${signaturePrefix}${isUnitedStatesPerson ? ' (please make sure you sign form as your name appears on your Social Security card.)' : ''}`;

    if (values.currentStep !== EmployeePages.SUMMARY) {
        return null;
    }
    return (
        <div id="overall-summary" ref={summary}>
            <Panel headerText={`Review and submit ${ownsClaim ? 'your' : 'this'} claim.`}
                   panelContent={
                       <PdsGrid variant="2up">
                           <PdsGridItem style={{borderRightStyle: "solid", borderColor: "#e6e6e6", borderWidth: "1px"}}>
                               {steps?.map(s => {
                                   const page = s.props.label as EmployeePages;
                                   if ([EmployeePages.START, EmployeePages.SUMMARY, EmployeePages.FINISH].includes(page)) {
                                       return <></>
                                   }
                                   return stepSummary(stepRefs, stepChangeCallback, page);
                               })}
                           </PdsGridItem>
                           <PdsGridItem>
                               <h3 className="pds-u-margin-top-0">
                                   Is everything
                                   accurate?{ownsClaim ? " Submit your claim, and we'll begin our process within 1-2 business days." : ""}
                               </h3>
                               <div className="pds-u-padding-bottom-8 flex-gap">
                                   {isUnitedStatesPerson && <div>
                                       <b>
                                           The Internal Revenue Service does not require your consent to any provision
                                           of this
                                           document other than the certifications required to avoid backup withholding.
                                       </b>
                                   </div>}
                                   <Agreements/>
                                   {needsSignature &&
                                       <Signature {...props} label={signatureLabel} id={signatureId} required={true}/>}
                                   <Button type="submit"
                                           buttonContent={isSubmitting ?
                                               <LoadingSpinner spinnerText="Submitting"/> : "Submit Claim"}
                                           buttonProps={agreementsNotAgreedTo || signatureRequiredAndNotSigned || isSubmitting
                                               ? {disabled: true}
                                               : {}}>
                                   </Button>
                               </div>
                               <NextSteps steps={props.nextSteps}/>
                           </PdsGridItem>
                       </PdsGrid>}
            >
            </Panel>
        </div>
    )

    function agreementNotAgreedTo(values: FormikValues, agreement: Agreement) {
        return !values[Agreement.findAgreementNameFor(agreement)]
    }

    function stepSummary(stepRefs: React.MutableRefObject<any>[],
                         stepChangeCallback: Function | undefined, page: EmployeePages) {
        const stepIndex = findStepIndexByName(steps, page);
        const stepRef = stepRefs[stepIndex]
        return <div id={`${_.kebabCase(page)}-summary`} className="pds-u-padding-bottom-8">
            {buildHeaderLink(stepIndex, stepChangeCallback, page)}
            {indent(summarize(stepRef.current, values as any))}
        </div>;
    }

    function buildHeaderLink(stepIndex: number, stepChangeCallback: Function | undefined,
                             stepName: EmployeePages) {
        return <PdsButton link="icon-left" size="sm" removeLinkPadding={true} type="button" onClick={() => {stepChangeCallback?.(stepIndex)}}>
            <PdsIconEdit slot="icon-left"/>
            <span>{stepName}</span>
        </PdsButton>;
    }

    function findStepIndexByName(steps: React.ReactElement<FormikStepProps>[], stepName: EmployeePages) {
        return steps.findIndex((step) => step.props.label === stepName);
    }

    function indent(toIndent: JSX.Element) {
        return <div className="pds-u-padding-left-24">
            {toIndent}
        </div>
    }
};

export default Summary;
